import { useRef, useState } from "react";
import { ChevronUp, ChevronDown, GripVertical } from "lucide-react";

const BTN = "h-4 w-4 flex items-center justify-center rounded text-slate-300 hover:text-blue-600 disabled:opacity-20 disabled:hover:text-slate-300";

// Mouse drag-and-drop reordering for list rows: spread `row(i)` on each row wrapper, render <DragHandle {...handle(i)} />.
export function useRowDrag(onMove) {
  const from = useRef(null);
  const [over, setOver] = useState(null);
  const row = (i) => ({
    onDragOver: (e) => { if (from.current !== null) { e.preventDefault(); setOver(i); } },
    onDragLeave: () => setOver((o) => (o === i ? null : o)),
    onDrop: (e) => { e.preventDefault(); if (from.current !== null && from.current !== i) onMove(from.current, i); from.current = null; setOver(null); },
  });
  const rowClass = (i) => (over === i && from.current !== i ? "ring-2 ring-blue-300 rounded-md" : "");
  const handle = (i) => ({
    draggable: true,
    onDragStart: (e) => { from.current = i; e.dataTransfer.effectAllowed = "move"; },
    onDragEnd: () => { from.current = null; setOver(null); },
  });
  return { row, handle, rowClass };
}

export function DragHandle({ testid, ...props }) {
  return (
    <span {...props} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0" title="Drag to reorder" data-testid={testid}>
      <GripVertical className="h-4 w-4" />
    </span>
  );
}

// ▲▼ reorder controls for a list row (mouse only — keyboard users use Alt+↑/↓ inside the row).
export function MoveButtons({ i, count, onMove, prefix }) {
  return (
    <span className="flex flex-col shrink-0" title="Reorder (Alt+↑/↓ inside the row)">
      <button type="button" tabIndex={-1} className={BTN} disabled={i === 0} onClick={() => onMove(i, i - 1)} data-testid={`${prefix}-up-${i}`}><ChevronUp className="h-3.5 w-3.5" /></button>
      <button type="button" tabIndex={-1} className={BTN} disabled={i === count - 1} onClick={() => onMove(i, i + 1)} data-testid={`${prefix}-down-${i}`}><ChevronDown className="h-3.5 w-3.5" /></button>
    </span>
  );
}

// Row-level keydown: Alt+↑/↓ moves the row and keeps focus on the same field of the moved row.
export function altArrowMove(i, count, onMove, containerRef, fieldSelector) {
  return (e) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const to = e.key === "ArrowUp" ? i - 1 : i + 1;
    if (to < 0 || to >= count) return;
    onMove(i, to);
    requestAnimationFrame(() => containerRef.current?.querySelector(fieldSelector(to))?.focus());
  };
}
